import React, { useMemo, useRef, useEffect } from 'react';
import RecapSection from '../RecapSection';
import UmlautHelper from './UmlautHelper';
import ReportSoalButton from '../../../LanguageLearning/ReportSoalButton';
import { ReviewQuestionHeader } from '../shared/ReviewQuestionHeader';

// Get base URL for audio
const getBaseURL = () => {
  return process.env.REACT_APP_API_URL || 'http://localhost:3000';
};

const QuestionAndAnswerReview = ({ question, userAnswer = {}, correctAnswer = {}, isCorrect, feedback, explanation, recap = '', annotations = [], langCode = null, reportProps = null }) => {
  console.log('🔍 QuestionAndAnswerReview - Props:', {
    question,
    userAnswer,
    correctAnswer,
    isCorrect,
    feedback,
    explanation,
  });

  // Get readingText from multiple sources and keep the largest one
  const questionText = question.text || '';
  const questionPassage = question.passage || '';
  const questionReadingText = question['reading-text'] || '';
  const questionContext = question.context || '';
  let storagePassage = '';
  
  // Try to get passage from localStorage
  try {
    const qpSessionRaw = localStorage.getItem("qp-session");
    if (qpSessionRaw) {
      const parsed = JSON.parse(qpSessionRaw);
      storagePassage = parsed.passage || '';
    } else {
      const qpPracticeRaw = localStorage.getItem("qp-practice");
      if (qpPracticeRaw) {
        const parsed = JSON.parse(qpPracticeRaw);
        storagePassage = parsed.passage || '';
      }
    }
  } catch (error) {
    // Ignore localStorage errors
  }
  
  // Keep the largest readingText from all sources
  const allSources = [questionText, questionPassage, questionReadingText, questionContext, storagePassage];
  const readingText = allSources.reduce((largest, current) => 
    (current && current.length > (largest || '').length) ? current : largest, '');
  
  const audioRef = useRef(null);
  const activeAnnotationRef = useRef(null);
  
  // Handle questions - can be array of strings or array of objects
  const questions = question.questions || question['sub-questions'] || [];
  
  // Transform questions array
  const questionsArray = useMemo(() => {
    if (!Array.isArray(questions) || questions.length === 0) {
      console.log('🔍 QuestionAndAnswerReview - No questions found:', { question, questions });
      return [];
    }
    
    return questions.map((q, index) => {
      if (typeof q === 'string') {
        return q;
      }
      return q.text || q.question || q['text-soal'] || '';
    }).filter(q => q && q.trim().length > 0);
  }, [questions, question]);

  // Normalize user answers - handle both array and object formats
  const userAnswers = useMemo(() => {
    if (!userAnswer) return {};
    
    // If it's already an array, convert to object with index keys
    if (Array.isArray(userAnswer)) {
      const answersObj = {};
      userAnswer.forEach((answer, index) => {
        answersObj[index] = answer;
      });
      return answersObj;
    }
    
    // If it's an object, use it directly
    if (typeof userAnswer === 'object') {
      return userAnswer;
    }
    
    // Fallback to empty object
    return {};
  }, [userAnswer]);

  // Build annotations map from question.annotations array
  const annotationsMap = useMemo(() => {
    const map = {};
    
    // First, try to get annotations from question.annotations array
    if (question.annotations && Array.isArray(question.annotations)) {
      console.log('🎵 QuestionAndAnswerReview - Raw annotations:', question.annotations);
      
      question.annotations.forEach((annotation) => {
        const annId = annotation['ann-id'] || annotation.annId || annotation.id;
        const streamUrl = annotation['stream-url'] || annotation.streamUrl || annotation.stream_url;
        const annType = annotation['ann-type'] || annotation.annType || annotation.type;
        
        console.log('🎵 QuestionAndAnswerReview - Processing annotation:', {
          annId,
          streamUrl,
          annType,
          rawAnnotation: annotation
        });
        
        // If streamUrl exists, treat it as audio annotation (for new format without ann-type)
        if (annId && streamUrl) {
          map[annId] = {
            annId,
            streamUrl,
            annType: annType || 'audio',
          };
        }
      });
    }
    
    // Also parse HTML for data-ann-id attributes (fallback)
    if (readingText && typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
      try {
        const parser = new window.DOMParser();
        const doc = parser.parseFromString(readingText, 'text/html');
        const nodes = doc.querySelectorAll('[data-ann-id]');

        nodes.forEach((node) => {
          const annId = node.getAttribute('data-ann-id');
          const annPath = node.getAttribute('data-ann-path');
          const annType = node.getAttribute('data-ann-type');

          // Only add if not already in map (from annotations array)
          // If annPath exists, treat it as audio (for new format)
          if (annId && annPath && !map[annId]) {
            // Convert data-ann-path to streamUrl
            const baseURL = getBaseURL();
            const streamUrl = `${baseURL}/${annPath}`;
            
            console.log('🎵 QuestionAndAnswerReview - Fallback annotation from HTML:', {
              annId,
              annPath,
              baseURL,
              streamUrl
            });
            
            map[annId] = {
              annId,
              streamUrl,
              annType: annType || 'audio',
            };
          }
        });
      } catch (error) {
        console.warn('QuestionAndAnswerReview: failed to parse annotations from HTML', error);
      }
    }

    console.log('🎵 QuestionAndAnswerReview - Final annotationsMap:', map);
    return map;
  }, [question.annotations, readingText]);

  // Helper function to enhance HTML text with annotation support
  const enhanceHtmlWithAnnotations = (htmlText) => {
    if (!htmlText || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      return htmlText;
    }

    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');

      nodes.forEach((node) => {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]) {
          // Add required classes
          if (!node.classList.contains('practice-annotation')) {
            node.classList.add('practice-annotation');
          }
          if (!node.classList.contains('annotation-hidden')) {
            node.classList.add('annotation-hidden');
          }
          if (!node.classList.contains('annotation')) {
            node.classList.add('annotation');
          }
          
          // Add accessibility attributes
          if (!node.hasAttribute('tabindex')) {
            node.setAttribute('tabindex', '0');
          }
          
          if (!node.hasAttribute('role')) {
            node.setAttribute('role', 'button');
          }
          
          if (!node.hasAttribute('aria-label')) {
            node.setAttribute('aria-label', `Play audio`);
          }
        }
      });

      return doc.body.innerHTML;
    } catch (error) {
      console.warn('QuestionAndAnswerReview: failed to enhance HTML', error);
      return htmlText;
    }
  };

  // Enhance HTML for reading text with annotation support
  const enhancedReadingText = useMemo(() => {
    return enhanceHtmlWithAnnotations(readingText);
  }, [readingText, annotationsMap]);

  // Enhance questions array with annotation support
  const enhancedQuestionsArray = useMemo(() => {
    return questionsArray.map((questionText) => {
      return enhanceHtmlWithAnnotations(questionText);
    });
  }, [questionsArray, annotationsMap]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const highlightAnnotation = (element) => {
    if (!element) return;

    if (activeAnnotationRef.current && activeAnnotationRef.current !== element) {
      activeAnnotationRef.current.classList.remove('practice-annotation-active');
    }

    activeAnnotationRef.current = element;
    element.classList.add('practice-annotation-active');
  };

  const playAnnotationAudio = (annotation) => {
    const streamUrl = annotation?.streamUrl;
    
    console.log('🎵 QuestionAndAnswerReview - Attempting to play audio:', {
      annotation,
      streamUrl,
      hasStreamUrl: !!streamUrl
    });
    
    if (!streamUrl) {
      console.warn('QuestionAndAnswerReview: No streamUrl found in annotation:', annotation);
      return;
    }

    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.addEventListener('ended', () => {
        if (activeAnnotationRef.current) {
          activeAnnotationRef.current.classList.remove('practice-annotation-active');
          activeAnnotationRef.current = null;
        }
      });
      audioRef.current.addEventListener('error', (e) => {
        console.error('QuestionAndAnswerReview: Audio playback error:', e);
        console.error('QuestionAndAnswerReview: Failed audio URL:', streamUrl);
      });
    }

    const audioEl = audioRef.current;
    audioEl.pause();
    audioEl.src = streamUrl;
    audioEl.currentTime = 0;
    
    console.log('🎵 QuestionAndAnswerReview - Audio element src set to:', audioEl.src);
    
    audioEl
      .play()
      .catch((error) => {
        console.error('QuestionAndAnswerReview: failed to play annotation audio', error);
        console.error('QuestionAndAnswerReview: Failed URL details:', {
          originalUrl: streamUrl,
          audioElementSrc: audioEl.src,
          audioElementError: audioEl.error
        });
      });
  };

  const findAnnotationTrigger = (event) => {
    const target = event.target;
    if (target && typeof target.closest === 'function') {
      return target.closest('[data-ann-id]');
    }
    return null;
  };

  const handleAnnotationTrigger = (event) => {
    if (!annotationsMap || Object.keys(annotationsMap).length === 0) {
      return;
    }

    const trigger = findAnnotationTrigger(event);
    if (!trigger) {
      return;
    }

    const annId = trigger.getAttribute('data-ann-id');
    if (!annId || !annotationsMap[annId]) {
      return;
    }

    const annotation = annotationsMap[annId];
    highlightAnnotation(trigger);
    playAnnotationAudio(annotation);
  };

  const handleAnnotationKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleAnnotationTrigger(event);
  };

  const hasAnnotations = annotationsMap && Object.keys(annotationsMap).length > 0;

  return (
    <div className="bg-[var(--card-bg)] rounded-xl p-6 mb-6 max-md:py-2 max-md:px-0">
      <ReviewQuestionHeader
        questionType="Question and Answer"
        currentQuestionNumber={reportProps?.currentQuestionNumber}
        totalQuestions={reportProps?.totalQuestions}
        score={reportProps?.score}
        reportSlot={reportProps ? (
          <ReportSoalButton
            type={reportProps.reportType}
            prosetId={reportProps.prosetId}
            soalId={reportProps.soalId}
            allowReReport={true}
            persistReportedState={true}
            reportText="Report this question"
          />
        ) : null}
      />

      <div className="flex flex-col gap-5">
        {readingText && (
          <div className="mb-3 p-4 md:p-4 max-md:p-4 max-md:mx-0 max-md:w-full bg-[#eff6ff] dark:bg-[rgba(59,130,246,0.1)] rounded-lg border-l-4 border-blue-500">
            <h4 className="text-base font-bold mb-3 text-blue-500 flex items-center">Reading Text:</h4>
            <div
              className="text-[15px] leading-[1.7] text-[var(--text-primary)] [&_*]:text-[var(--text-primary)]"
              onClick={hasAnnotations ? handleAnnotationTrigger : undefined}
              onKeyDown={hasAnnotations ? handleAnnotationKeyDown : undefined}
              dangerouslySetInnerHTML={{ __html: enhancedReadingText }}
            />
          </div>
        )}

        <div className="flex flex-col gap-3">
          {questionsArray.length === 0 ? (
            <div className="py-4">
              <p className="text-[var(--text-tertiary)] italic text-sm">No questions data available</p>
              <p className="text-xs text-[#999] mt-2">
                Debug: Check console for question data structure
              </p>
            </div>
          ) : (
            questionsArray.map((questionText, index) => {
            const userAnswerText = userAnswers[index] || userAnswers[index.toString()] || '';
            const enhancedQuestionText = enhancedQuestionsArray[index] || questionText;
            const hasQuestionAnnotations = annotationsMap && Object.keys(annotationsMap).length > 0;
            
            // Get feedback for this sub-question - if feedback is array, get item at index
            const feedbackForThisQuestion = Array.isArray(feedback) 
              ? (feedback[index] || '') 
              : (typeof feedback === 'object' && feedback !== null 
                  ? feedback[index] || feedback[index.toString()] || ''
                  : (index === 0 ? feedback : ''));
            
            return (
              <div key={index} className="flex flex-col gap-4 mb-5 pb-5 border-b border-gray-200 dark:border-[var(--border-color)] last:mb-0 last:pb-0 last:border-b-0">
                <div className="p-4 md:p-4 max-md:p-4 max-md:mx-0 max-md:w-full bg-[var(--bg-tertiary)] rounded-lg border-l-4 border-[#146e69] dark:border-[#2dd4bf]">
                  <h4 className="text-sm font-bold mb-2 text-[#146e69] dark:text-[#2dd4bf]">Question {index + 1}:</h4>
                  <div
                    className="text-[15px] leading-[1.7] text-[var(--text-primary)] [&_*]:text-[var(--text-primary)]"
                    onClick={hasQuestionAnnotations ? handleAnnotationTrigger : undefined}
                    onKeyDown={hasQuestionAnnotations ? handleAnnotationKeyDown : undefined}
                    dangerouslySetInnerHTML={{ __html: enhancedQuestionText }}
                  />
                </div>
                
                <div className="p-4 md:p-4 max-md:p-4 max-md:mx-0 max-md:w-full bg-[var(--card-bg)] rounded-lg border border-[var(--border-color)]">
                  <h4 className="text-sm font-bold mb-3 text-[var(--text-primary)] uppercase tracking-wide flex items-center gap-2">
                    Your Answer:
                    <UmlautHelper showTooltip={false} langCode={langCode} />
                  </h4>
                  {userAnswerText ? (
                    <p className="text-[15px] leading-[1.6] text-[var(--text-primary)]">{userAnswerText}</p>
                  ) : (
                    <p className="text-[var(--text-tertiary)] italic text-sm">No answer provided</p>
                  )}
                </div>

                {/* Feedback per sub-question - di bawah Your Answer */}
                {feedbackForThisQuestion && (
                  <div className="mt-3 p-5 bg-[#fffbeb] dark:bg-[rgba(251,191,36,0.1)] rounded-lg border-l-4 border-amber-500">
                    <h4 className="text-base font-bold mb-3 text-orange-600 dark:text-orange-400 flex items-center">💡 Feedback:</h4>
                    <div className="text-[15px] leading-[1.7] text-gray-800 dark:text-gray-200">
                      <p className="m-0">{feedbackForThisQuestion}</p>
                    </div>
                  </div>
                )}
              </div>
            );
            })
          )}
        </div>

        {/* Explanation - di atas recap */}
        {explanation && (
          <div className={feedback ? 'mt-4' : 'mt-0'} style={{ marginBottom: 0 }}>
            <div className="bg-gradient-to-br from-[#fff9e6] to-[#ffeaa7] dark:from-[#3d3520] dark:to-[#4a3d1a] p-5 rounded-xl border-l-4 border-[#ffd93d] max-md:p-4 max-md:mx-0 max-md:w-full">
              <h3 className="text-base font-bold text-[#d68910] dark:text-[#fbbf24] mb-3 flex items-center gap-2">Penjelasan</h3>
              <p className="text-[15px] text-[var(--text-primary)] leading-relaxed m-0">{explanation}</p>
            </div>
          </div>
        )}

        <RecapSection recap={recap} annotations={annotations} question={question} />
      </div>
    </div>
  );
};

export default QuestionAndAnswerReview;

